import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { ShieldCheck, CheckCircle2, PenLine, Eraser } from "lucide-react";
import logo from "@/assets/murphy-logo.png";

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const pos = (e) => {
    const cv = canvasRef.current;
    const r = cv.getBoundingClientRect();
    const t = e.touches && e.touches[0] ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (cv.width / r.width), y: (t.clientY - r.top) * (cv.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const c = canvasRef.current.getContext("2d"); const p = pos(e); c.beginPath(); c.moveTo(p.x, p.y); };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const c = canvasRef.current.getContext("2d"); const p = pos(e);
    c.lineTo(p.x, p.y); c.strokeStyle = "#111"; c.lineWidth = 2; c.lineCap = "round"; c.stroke();
  };
  const end = () => { if (drawing.current) { drawing.current = false; onChange(canvasRef.current.toDataURL("image/png")); } };
  const clear = () => { const cv = canvasRef.current; cv.getContext("2d").clearRect(0, 0, cv.width, cv.height); onChange(null); };
  return (
    <div>
      <div className="rounded-lg bg-white border border-border overflow-hidden">
        <canvas ref={canvasRef} width={520} height={140} className="w-full touch-none cursor-crosshair"
          data-testid="signature-canvas"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      </div>
      <button type="button" onClick={clear} className="text-xs text-muted-foreground mt-1 flex items-center gap-1 hover:text-foreground"><Eraser className="w-3 h-3" /> Tøm signatur</button>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen noise-bg py-10 px-4">
      <Toaster position="bottom-right" theme="dark" />
      <div className="max-w-3xl mx-auto relative z-10">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <img src={logo} alt="Murphy" className="h-7 w-auto brightness-0 invert" />
          <span className="text-muted-foreground text-sm">Digital signering</span>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function SignAgreement() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [code, setCode] = useState("");
  const [verified, setVerified] = useState(false);
  const [doc, setDoc] = useState(null);
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [sig, setSig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/sign/${token}/info`).then((r) => {
      setInfo(r.data); setVerified(r.data.verified); setDone(r.data.signed);
      if (r.data.signer_name) setName(r.data.signer_name);
    }).catch((e) => setError(apiError(e)));
  }, [token]);

  const loadDoc = useCallback(() => api.get(`/sign/${token}`).then((r) => setDoc(r.data)).catch((e) => setError(apiError(e))), [token]);
  useEffect(() => { if (verified && !done) loadDoc(); }, [verified, done, loadDoc]);

  const verify = async () => {
    try { await api.post(`/sign/${token}/verify`, { code }); setVerified(true); }
    catch (e) { toast.error(apiError(e)); }
  };
  const submit = async () => {
    if (name.trim().length < 2) { toast.error("Skriv inn fullt navn"); return; }
    if (!consent) { toast.error("Du må bekrefte at du signerer avtalen"); return; }
    setSubmitting(true);
    try {
      await api.post(`/sign/${token}`, { signer_name: name, consent, signature_image: sig });
      setDone(true);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  const isUpload = info?.doc_type === "upload";

  if (error) return <Shell><div className="bg-card border border-border rounded-xl p-8 text-center" data-testid="sign-error"><p className="text-destructive">{error}</p></div></Shell>;
  if (!info) return <Shell><p className="text-center text-muted-foreground">Laster…</p></Shell>;

  if (done) return (
    <Shell>
      <div className="bg-card border border-border rounded-xl p-10 text-center" data-testid="sign-done">
        <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
        <h1 className="font-display text-2xl mb-2">Takk – avtalen er signert!</h1>
        <p className="text-muted-foreground">{isUpload ? "Dokumentet" : "Oppdragsavtalen"} for <strong>{info.customer_name}</strong> er nå elektronisk signert. Du vil motta en bekreftelse på e-post.</p>
      </div>
    </Shell>
  );

  if (!verified) return (
    <Shell>
      <div className="bg-card border border-border rounded-xl p-8 max-w-md mx-auto" data-testid="sign-verify">
        <ShieldCheck className="w-10 h-10 text-white mb-4" />
        <h1 className="font-display text-xl mb-1">Bekreft identitet</h1>
        <p className="text-sm text-muted-foreground mb-5">Skriv inn engangskoden fra e-posten for å åpne {isUpload ? "dokumentet" : "oppdragsavtalen"} for {info.customer_name}.</p>
        <Label className="label-caps">Engangskode</Label>
        <Input value={code} onChange={(e) => setCode(e.target.value)} className="mt-1 bg-secondary border-border" data-testid="sign-code-input" placeholder="6-sifret kode" />
        <Button onClick={verify} className="w-full mt-4 bg-white text-black hover:bg-white/90" data-testid="sign-verify-btn">Åpne {isUpload ? "dokumentet" : "avtalen"}</Button>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div className="bg-card border border-border rounded-xl p-5 mb-4">
        <p className="label-caps">{isUpload ? "Dokument til signering" : "Oppdragsavtale"}</p>
        <p className="font-display text-lg">{doc?.agreement_id} · {info.customer_name}</p>
      </div>
      {isUpload
        ? <iframe title="Dokument" src={`${API}/sign/${token}/file`} className="w-full h-[560px] rounded-xl border border-border bg-white mb-4" data-testid="sign-document-frame" />
        : doc?.html && <iframe title="Avtale" srcDoc={doc.html} sandbox="" className="w-full h-[520px] rounded-xl border border-border bg-white mb-4" data-testid="sign-agreement-frame" />}

      <div className="bg-card border border-border rounded-xl p-6">
        <h2 className="font-display text-lg mb-4 flex items-center gap-2"><PenLine className="w-5 h-5" /> Signer {isUpload ? "dokumentet" : "avtalen"}</h2>
        <div className="space-y-4">
          <div>
            <Label className="label-caps">Fullt navn</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 bg-secondary border-border" data-testid="sign-name-input" placeholder="Ditt fulle navn" />
          </div>
          <div>
            <Label className="label-caps">Signatur (valgfritt – tegn med mus/finger)</Label>
            <div className="mt-1"><SignaturePad onChange={setSig} /></div>
          </div>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={consent} onCheckedChange={setConsent} data-testid="sign-consent" className="mt-0.5" />
            <span>Jeg bekrefter at jeg har lest og godtar {isUpload ? "dokumentet" : "oppdragsavtalen"}, og signerer {isUpload ? "det" : "den"} elektronisk{isUpload ? "" : ` på vegne av ${info.customer_name}`}. Jeg er innforstått med at signaturen er bindende.</span>
          </label>
          <Button onClick={submit} disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sign-submit-btn">
            {submitting ? "Signerer…" : `Signer ${isUpload ? "dokumentet" : "avtalen"}`}
          </Button>
        </div>
      </div>
    </Shell>
  );
}
